import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  ChatBubbleIcon,
  CodeIcon,
  ExclamationTriangleIcon,
  MixIcon,
  PaperPlaneIcon,
} from '@radix-ui/react-icons';
import { useForm } from 'react-hook-form';
import { chatConversationSchema, ChatConversationSchema } from './type';
import { zodResolver } from '@hookform/resolvers/zod';
import { Form } from '@/components/ui/form';
import PlaygroundTextGenerationSetting from './setting';
import { useMutation } from '@tanstack/react-query';
import axios from 'axios';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { useEffect, useRef, useState } from 'react';
import { isNotEmpty } from '@/utils';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import PlaygroundTextGenerationAPI from './api';
import urlJoin from 'url-join';

type PlaygroundTextGenerationProps = {
  title?: string;
  model: string;
  endpoint: string;
  apikey: string;
};

const PlaygroundTextGeneration = (props: PlaygroundTextGenerationProps) => {
  const { title = 'Text Generation', apikey, endpoint, model } = props;

  const [message, setMessage] = useState('');
  const [selectTab, setSelectTab] = useState('chat');

  const endRef = useRef<HTMLDivElement>(null);

  const form = useForm<ChatConversationSchema>({
    resolver: zodResolver(chatConversationSchema),
    defaultValues: {
      messages: [],
      temperature: 0.5,
      maxTokens: 512
    },
  });

  const { mutate, isPending, error } = useMutation({
    mutationFn: async (params: ChatConversationSchema) => {
      const { data } = await axios.post(
        urlJoin(endpoint, '/chat/completions'),
        {
          model: model,
          temperature: params?.temperature,
          messages: [
            {
              role: 'system',
              content: params?.systemPrompt,
            },
            ...params.messages,
          ].filter((msg) => isNotEmpty(msg.content)),
          max_tokens: params?.maxTokens,
        },
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${apikey}`,
          },
        }
      );
      return data;
    },
    onSuccess: (data) => {
      const response = data?.choices?.[0]?.message?.content;
      const newMessages = [
        ...form.getValues('messages'),
        { role: 'assistant', content: response },
      ];
      form.setValue('messages', newMessages);

    },
    onError: (error) => {
      console.error(error);
    }
  });
  const messages = form.watch('messages');

  const onSubmit = async (data: ChatConversationSchema) => {
    if (!message) return;
    const newMessages = [...data.messages, { role: 'user', content: message }];
    form.setValue('messages', newMessages);
    setMessage('');
    mutate({
      messages: newMessages,
      temperature: data.temperature ?? undefined,
      maxTokens: data.maxTokens ?? undefined,
      systemPrompt: data.systemPrompt,
    });
  };

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (
        (event.metaKey && event.key === 'Enter') || // cmd + return (Mac)
        (event.altKey && event.key === 'Enter') // alt + enter (Windows)
      ) {
        event.preventDefault();
        form.handleSubmit(onSubmit)();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  return (
    <div>
      <Form {...form}>
        <form className="flex flex-grow" onSubmit={form.handleSubmit(onSubmit)}>
          <Card className="h-full w-full items-stretch lg:flex">
            <div className="flex-none rounded-t-xl lg:flex lg:min-h-[12rem] lg:w-72 lg:flex-col lg:rounded-tr-none lg:border-r">
              <div className="flex h-[3.25rem] flex-none items-center rounded-t-xl border-b px-4 lg:rounded-tr-none">
                <div className="flex items-center gap-1">
                  <MixIcon />
                  <span className="font-semibold lg:mr-auto">Playground</span>
                </div>
              </div>
              <div className="max-h-72 overflow-y-auto px-4 py-4 lg:max-h-none">
                <div className="flex items-center pb-2 text-xs font-medium text-muted-foreground lg:block">
                  Parameters
                </div>
                <div className="mt-2 flex w-full flex-col gap-2">
                  <PlaygroundTextGenerationSetting />
                </div>
              </div>
            </div>
            <div className="h-full w-full flex-1 overflow-hidden">
              <div className="h-full flex-col lg:flex">
                <div className="flex h-[3.25rem] flex-none items-center justify-between border-b px-5 lg:px-7">
                  <span className="font-semibold">{title}</span>
                  <div>
                    <Tabs value={selectTab} onValueChange={setSelectTab}>
                      <TabsList className="h-full">
                        <TabsTrigger value="chat" className="h-6">
                          <ChatBubbleIcon className="mr-1 w-3" />
                          Chat
                        </TabsTrigger>
                        <TabsTrigger value="api" className="h-6">
                          <CodeIcon className="mr-1" />
                          API
                        </TabsTrigger>
                      </TabsList>
                    </Tabs>
                  </div>
                </div>
                <div className="flex-1 overflow-y-auto overflow-x-hidden px-5 pb-8 pt-6 lg:px-7">
                  {selectTab === 'chat' && (
                    <>
                      <div className="h-64 overflow-y-auto rounded-md border leading-tight">
                        <div className="flex flex-col items-end space-y-4 p-3">
                          {messages.map((msg) =>
                            msg.role === 'user' ? (
                              <div className="ml-7 rounded-lg bg-primary px-3 py-2 text-primary-foreground">
                                {msg.content}
                              </div>
                            ) : (
                              <div className="mr-7 place-self-start rounded-lg bg-gray-50 px-3 py-2 dark:bg-gray-800">
                                {msg.content}
                              </div>
                            )
                          )}
                        </div>
                        <div ref={endRef} className="!m-0"></div>
                      </div>
                      <div className="mt-2 flex gap-2">
                        <Input
                          placeholder="Your message here..."
                          className="w-full"
                          value={message}
                          onChange={(e) => setMessage(e.target.value)}
                        />
                        <Button
                          variant={'secondary'}
                          type="submit"
                          endIcon={<PaperPlaneIcon />}
                          disabled={isPending || !message}
                        >
                          Send
                        </Button>
                      </div>
                    </>
                  )}
                  {selectTab === 'api' && (
                    <>
                      <PlaygroundTextGenerationAPI
                        model={model}
                        apiKey={apikey}
                        endpoint={endpoint}
                      />
                    </>
                  )}
                  {error && (
                    <div className="pt-6">
                      {axios.isAxiosError(error) ? (
                        <Alert variant="destructive">
                          <ExclamationTriangleIcon className="h-4 w-4" />
                          <AlertTitle>{error.code}</AlertTitle>
                          <AlertDescription>{error.message}</AlertDescription>
                          <AlertDescription>
                            {JSON.stringify(error?.response?.data)}
                          </AlertDescription>
                        </Alert>
                      ) : (
                        <Alert variant="destructive">
                          <ExclamationTriangleIcon className="h-4 w-4" />
                          <AlertTitle>Oops! 😥🙇‍♂️</AlertTitle>
                          <AlertDescription>
                            Something went wrong.{' '}
                          </AlertDescription>
                        </Alert>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </Card>
        </form>
      </Form>
    </div>
  );
};

export default PlaygroundTextGeneration;